import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { AppBadge, AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCheck,
  IconError,
  IconHourglass,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { getPayment } from "../api";
import { formatTime, formatTzs } from "../format";
import type { Payment } from "../types";

/**
 * S2 — Verification result (handoff TASK-009, s2-verification-* boards).
 * Server-side reference + signature + idempotency checks; all three must
 * pass before allocation runs.
 *   verified  → continue to S3 allocation
 *   duplicate → links the ORIGINAL payment; never double-applied
 *   invalid   → exception sent to reconciliation (REC-YYYY-####)
 *   pending   → provider check still running (polls until resolved)
 */

function CheckRow({
  label,
  state,
}: {
  label: string;
  state: boolean | null;
}) {
  const cls = state === true ? "done" : state === false ? "missing" : "unknown";
  return (
    <div className={`row ${cls}`}>
      <span className="check-icon">
        {state === true ? (
          <IconCheck size={16} />
        ) : state === false ? (
          <IconError size={16} />
        ) : (
          <IconHourglass size={16} />
        )}
      </span>
      <span className="check-label">{label}</span>
      <span className="check-state">
        {state === true ? "Done" : state === false ? "Failed" : "Pending"}
      </span>
    </div>
  );
}

export function S2VerificationResult() {
  const { id = "" } = useParams();
  const paymentId = Number(id);
  const navigate = useNavigate();
  // F2: an invalid intake has no payment id — never fetch ".../payments/NaN".
  const validId = Number.isFinite(paymentId) && paymentId > 0;

  const query = useQuery({
    queryKey: ["payment", paymentId],
    queryFn: () => getPayment(paymentId),
    enabled: validId,
    retry: 1,
    // Poll while the provider check is still pending.
    refetchInterval: (q) =>
      q.state.data?.payment.status === "pending" ? 3_000 : false,
  });

  const payment: Payment | undefined = query.data?.payment;
  useFieldHeader(
    "Payment verification",
    payment ? `${payment.customer_name ?? "Customer"} — ${formatTzs(payment.amount_cents)}` : undefined,
  );
  useFieldJourney({ flow: "payments", step: 1, backTo: "/field/payments/new" });

  if (!validId) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>No payment reference</h2>
        <p>
          This screen needs a recorded payment. Invalid or unmatched payments go straight to
          reconciliation (REC-YYYY-####) and have no verification page.
        </p>
        <AppButton variant="secondary" type="button" onClick={() => navigate("/field/payments/new")}>
          Record a payment
        </AppButton>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Checking with provider…</h2>
      </div>
    );
  }

  if (query.isError || !payment) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Verification unavailable</h2>
        <p>{query.error instanceof Error ? query.error.message : "Could not load the payment."}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  const v = payment.verification;

  // ── Pending / queued-state variant ─────────────────────────────────────────
  if (payment.status === "pending" || !v) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Checking with provider…</h2>
        <p>
          {payment.payment_number ?? "Payment"} — {formatTzs(payment.amount_cents)}. Verification runs
          automatically; this screen updates when the provider responds.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="pg-checklist" aria-label="Verification checks">
        <CheckRow
          label={`Reference found at provider (${payment.method === "mobile_money" ? "M-Pesa" : "provider"})`}
          state={v.reference_found}
        />
        <CheckRow label="Signature valid" state={v.signature_valid} />
        <CheckRow
          label={v.first_seen === false ? "Idempotency: seen before" : "Idempotency: first time seen"}
          state={v.first_seen}
        />
      </div>

      {payment.status === "verified" ? (
        <>
          <div className="pg-verify-summary">
            <AppBadge tone="success">
              <IconCheck size={12} /> Verified
            </AppBadge>
            <span className="vs-meta">
              {payment.payment_number ?? "Payment"}
              {payment.verified_at ? ` · verified today ${formatTime(payment.verified_at)}` : ""}
            </span>
          </div>
          <div className="pg-actions">
            <AppButton
              type="button"
              onClick={() => navigate(`/field/payments/${payment.id}/allocation`)}
            >
              Continue to allocation
            </AppButton>
          </div>
        </>
      ) : payment.status === "duplicate" ? (
        <>
          <div className="pg-gono-banner no-go">
            <IconWarning size={24} />
            <div>
              Duplicate detected
              <div className="gono-sub">
                This payment was already recorded
                {payment.duplicate_of_payment_number
                  ? ` as ${payment.duplicate_of_payment_number}`
                  : ""}{" "}
                — it is NOT applied twice.
              </div>
            </div>
          </div>
          <div className="pg-actions">
            {payment.duplicate_of_payment_id !== null ? (
              <AppButton
                type="button"
                onClick={() =>
                  navigate(`/field/payments/${payment.duplicate_of_payment_id}/allocation`)
                }
              >
                View original payment
              </AppButton>
            ) : null}
            <AppButton
              variant="secondary"
              type="button"
              onClick={() => navigate("/field/payments/new")}
            >
              Record another payment
            </AppButton>
          </div>
        </>
      ) : (
        <>
          <div className="pg-inline-error" role="alert">
            <IconError size={14} />
            {v.failure_reason ?? "Verification failed"} — sent to reconciliation
            {payment.exception_number ? ` as ${payment.exception_number}` : ""}. The office will
            resolve it; no allocation was run.
          </div>
          <div className="pg-actions">
            <AppButton type="button" onClick={() => navigate("/field/payments/new")}>
              Record another payment
            </AppButton>
          </div>
        </>
      )}
    </>
  );
}
